import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform, ScrollView, TextInput } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useRouter, Redirect } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useBookmarkStore, useChatStore, useModalStore, useToastStore, useAuthStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import MetaHead from '@/components/MetaHead';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import useResponsiveness from '@/hooks/useResponsiveness';
import BookmarksRail from '@/components/Bookmarks/BookmarksRail';
import BookmarksEmptyMagazine from '@/components/Bookmarks/BookmarksEmptyMagazine';
import BookmarksSortDropdown from '@/components/Dropdowns/BookmarksSortDropdown';
import BookmarkFolderActions from '@/components/Bookmarks/BookmarkFolderActions';
import BookmarkCard from '@/components/Bookmarks/BookmarkCard';
import { getAncestorIds, getDescendantIds, getItemDepth, MAX_FOLDER_DEPTH, normalizeParentId } from '@/constants/utils/folder_tree';

const createStyles = (colors, palette, isXS) => StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  shell: {
    flex: 1,
    flexDirection: 'row',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  contentWrap: {
    flex: 1,
    maxWidth: 960,
    width: '100%',
    alignSelf: 'center',
  },
  chromeBar: {
    paddingHorizontal: isXS ? 16 : 24,
    paddingTop: isXS ? 20 : 28,
    paddingBottom: 16,
    gap: 18,
  },
  pageHeader: {
    gap: 4,
    marginBottom: 2,
  },
  pageTitle: {
    fontSize: 32,
    lineHeight: 38,
    letterSpacing: -0.8,
    fontWeight: '700',
  },
  search: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
  },
  topbarMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isXS ? 8 : 12,
    width: '100%',
  },
  searchInput: {
    flex: 1,
    color: colors.text_color_primary,
    fontSize: 14,
    minWidth: 0,
    paddingVertical: 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  pillsRow: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 4,
    width: '100%',
  },
  pill: {
    paddingHorizontal: 14,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.surface,
    borderColor: palette.border,
  },
  pillActive: {
    backgroundColor: palette.selected,
    borderColor: palette.activeBorder,
  },
  mobileFolderActions: {
    paddingTop: 6,
  },
  listPad: {
    paddingHorizontal: isXS ? 16 : 24,
    paddingBottom: isXS ? 24 : 40,
  },

  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: isXS ? 24 : 40,
    paddingTop: isXS ? 40 : 80,
  },
  emptyIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 14,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  emptyTitle: {
    marginBottom: 6,
    textAlign: 'center',
  },
  emptyDescription: {
    textAlign: 'center',
    maxWidth: 360,
  },
  emptyStepsCard: {
    marginTop: 22,
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
    gap: 12,
  },
  emptyStepsHeading: {
    marginBottom: 2,
  },
  emptyStepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  emptyStepIndex: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStepTextWrap: {
    flex: 1,
    gap: 2,
  },
  noResultsContainer: {
    alignItems: 'center',
    paddingTop: isXS ? 32 : 60,
    paddingHorizontal: 24,
  },
});

// ── Empty states ──

const EmptyState = React.memo(({ colors, styles, t, isFolder, searchQuery }) => {
  if (searchQuery) {
    return (
      <View style={styles.noResultsContainer}>
        <View style={styles.emptyIconContainer}>
          <Icon name="magnifying-glass" size={26} color={colors.text_color_secondary} />
        </View>
        <Text size="m" weight="semibold" style={styles.emptyTitle}>
          {t('pages.bookmarks.noSearchResults').replace('%QUERY%', searchQuery)}
        </Text>
      </View>
    );
  }
  const steps = isFolder ? null : [
    { icon: 'message-lines', titleKey: 'pages.bookmarks.emptyStep1Title', descKey: 'pages.bookmarks.emptyStep1Description' },
    { icon: 'bookmark', titleKey: 'pages.bookmarks.emptyStep2Title', descKey: 'pages.bookmarks.emptyStep2Description' },
    { icon: 'folder', titleKey: 'pages.bookmarks.emptyStep3Title', descKey: 'pages.bookmarks.emptyStep3Description' },
  ];

  return (
    <View style={styles.emptyContainer}>
      <View style={styles.emptyIconContainer}>
        <Icon name={isFolder ? 'folder' : 'bookmark'} size={26} color={colors.text_color_secondary} />
      </View>
      <Text size="m" weight="semibold" style={styles.emptyTitle}>
        {isFolder ? t('pages.bookmarks.emptyFolderTitle') : t('pages.bookmarks.emptyTitle')}
      </Text>
      <Text size="s" color="secondary" style={styles.emptyDescription}>
        {isFolder ? t('pages.bookmarks.emptyFolderDescription') : t('pages.bookmarks.emptyDescription')}
      </Text>
      {steps && (
        <View style={styles.emptyStepsCard}>
          <Text size="s" weight="semibold" style={styles.emptyStepsHeading}>
            {t('pages.bookmarks.emptyHowTitle')}
          </Text>
          {steps.map((step, idx) => (
            <View key={step.titleKey} style={styles.emptyStepRow}>
              <View style={styles.emptyStepIndex}>
                <Text size="xs" weight="semibold" color="secondary">{idx + 1}</Text>
              </View>
              <View style={styles.emptyStepTextWrap}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Icon name={step.icon} size={12} color={colors.text_color_secondary} />
                  <Text size="s" weight="medium">{t(step.titleKey)}</Text>
                </View>
                <Text size="xs" color="secondary">{t(step.descKey)}</Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
});

EmptyState.displayName = 'EmptyState';

// ── Main page ──

const BookmarksPage = React.memo(() => {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const [searchFocused, setSearchFocused] = useState(false);
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => createStyles(colors, palette, isXS), [colors, palette, isXS]);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);

  const bookmarks = useBookmarkStore(state => state.bookmarks);
  const pagination = useBookmarkStore(state => state.pagination);
  const isLoading = useBookmarkStore(state => state.isLoading);
  const hasInitiallyLoaded = useBookmarkStore(state => state.hasInitiallyLoaded);
  const fetchBookmarks = useBookmarkStore(state => state.fetchBookmarks);
  const removeBookmark = useBookmarkStore(state => state.removeBookmark);
  const unsaveMessage = useChatStore(state => state.unsaveMessage);

  const activeFolderId = useBookmarkStore(state => state.activeFolderId);
  const setActiveFolderId = useBookmarkStore(state => state.setActiveFolderId);
  const sort = useBookmarkStore(state => state.sort);
  const setSort = useBookmarkStore(state => state.setSort);
  const searchQuery = useBookmarkStore(state => state.searchQuery);
  const setSearchQuery = useBookmarkStore(state => state.setSearchQuery);

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((text) => {
    setLocalSearch(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(text.trim());
    }, 400);
  }, [setSearchQuery]);

  const handleSearchClear = useCallback(() => {
    setLocalSearch('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchQuery('');
  }, [setSearchQuery]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const folders = useBookmarkStore(state => state.folders);
  const foldersLoaded = useBookmarkStore(state => state.foldersLoaded);
  const fetchFolders = useBookmarkStore(state => state.fetchFolders);
  const createFolder = useBookmarkStore(state => state.createFolder);
  const renameFolder = useBookmarkStore(state => state.renameFolder);
  const updateFolderIcon = useBookmarkStore(state => state.updateFolderIcon);
  const deleteFolder = useBookmarkStore(state => state.deleteFolder);
  const moveFolder = useBookmarkStore(state => state.moveFolder);

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    // Both endpoints are per-user — skip for anonymous visitors and
    // render the sign-in CTA panel below instead.
    if (!hasUser) return;
    fetchBookmarks(1);
    if (!foldersLoaded) fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUser]);

  const handleUnsave = useCallback((conversationId, messageId) => {
    showConfirmationModal({
      title: t('pages.bookmarks.unsave'),
      message: t('pages.bookmarks.unsaveConfirm'),
      confirmText: t('pages.bookmarks.unsave'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          removeBookmark(conversationId, messageId);
          await unsaveMessage(conversationId, messageId);
        } catch (error) {
          fetchBookmarks(1);
        }
      },
    });
  }, [showConfirmationModal, removeBookmark, unsaveMessage, fetchBookmarks, t]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !pagination.hasMore) return;
    setIsLoadingMore(true);
    try {
      await fetchBookmarks(pagination.current + 1);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, pagination, fetchBookmarks]);

  const handleCreateFolder = useCallback((parentFolder = null) => {
    const parentId = parentFolder?.id || null;
    openModal('BookmarkFolderFormModal', {
      title: parentId ? t('pages.bookmarks.newSubfolder') : t('pages.bookmarks.newFolder'),
      onSave: async (name, icon) => {
        await createFolder(name, icon, parentId);
      },
    });
  }, [openModal, createFolder, t]);

  const handleCreateSubfolder = useCallback((folder) => {
    if (getItemDepth(folders, folder.id) >= MAX_FOLDER_DEPTH - 1) {
      addToast(t('pages.bookmarks.nestedTooDeep'), 'error');
      return;
    }
    handleCreateFolder(folder);
  }, [folders, handleCreateFolder, addToast, t]);

  const handleMoveFolder = useCallback((folder) => {
    const excludeIds = [folder.id, ...getDescendantIds(folders, folder.id)];
    openModal('BookmarkFolderPickerModal', {
      mode: 'pickParent',
      allowRoot: true,
      excludeIds,
      currentParentId: folder.parentId || null,
      title: t('pages.bookmarks.moveFolder'),
      onPick: async (parentId) => {
        await moveFolder(folder.id, parentId);
        addToast(t('pages.bookmarks.folderMoved'), 'success');
      },
    });
  }, [folders, openModal, moveFolder, addToast, t]);

  const handleRenameFolder = useCallback((folder) => {
    openModal('BookmarkFolderFormModal', {
      title: t('pages.bookmarks.renameFolder'),
      initialName: folder.name,
      initialIcon: folder.icon || 'folder',
      onSave: async (name, icon) => {
        await renameFolder(folder.id, name);
        if (icon !== (folder.icon || 'folder')) {
          await updateFolderIcon(folder.id, icon);
        }
      },
    });
  }, [openModal, renameFolder, updateFolderIcon, t]);

  const handleChangeIcon = useCallback((folder) => {
    openModal('BookmarkFolderFormModal', {
      title: t('pages.bookmarks.changeIcon'),
      initialName: folder.name,
      initialIcon: folder.icon || 'folder',
      onSave: async (name, icon) => {
        if (name !== folder.name) await renameFolder(folder.id, name);
        if (icon !== (folder.icon || 'folder')) await updateFolderIcon(folder.id, icon);
      },
    });
  }, [openModal, renameFolder, updateFolderIcon, t]);

  const handleDeleteFolder = useCallback((folder) => {
    showConfirmationModal({
      title: t('pages.bookmarks.deleteFolder'),
      message: t('pages.bookmarks.deleteFolderMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        await deleteFolder(folder.id);
      },
    });
  }, [showConfirmationModal, deleteFolder, t]);

  const renderItem = useCallback(({ item }) => (
    <BookmarkCard
      item={item}
      t={t}
      onUnsave={handleUnsave}
      isXS={isXS}
      folders={folders}
      activeFolderId={activeFolderId}
    />
  ), [t, handleUnsave, isXS, folders, activeFolderId]);

  const keyExtractor = useCallback((item) => `${item.conversationId}-${item.message?.id}`, []);

  const ListFooterComponent = useCallback(() => (
    pagination.hasMore ? (
      <View style={{ padding: 16 }}>
        <LoadMoreButton onPress={handleLoadMore} isLoading={isLoadingMore} isLarge />
      </View>
    ) : null
  ), [pagination.hasMore, isLoadingMore, handleLoadMore]);

  const showEmpty = hasInitiallyLoaded && bookmarks.length === 0 && !isLoading;
  const totalCount = pagination.count != null ? pagination.count : bookmarks.length;

  const handleFolderPress = useCallback((folderId) => {
    setActiveFolderId(folderId);
  }, [setActiveFolderId]);

  const activeFolder = useMemo(
    () => folders.find((f) => f.id === activeFolderId) || null,
    [folders, activeFolderId],
  );

  const handleEmptyMagazineCta = useCallback(() => {
    router.push('/app');
  }, [router]);

  const mobilePillItems = useMemo(() => {
    const items = [
      {
        id: '__all__',
        folderId: null,
        label: t('pages.bookmarks.allBookmarks'),
      },
    ];
    if (!activeFolderId) {
      folders.filter((f) => !normalizeParentId(f.parentId)).forEach((f) => {
        items.push({ id: f.id, folderId: f.id, label: f.name });
      });
      return items;
    }
    getAncestorIds(folders, activeFolderId).forEach((id) => {
      const folder = folders.find((f) => f.id === id);
      if (folder) items.push({ id: folder.id, folderId: folder.id, label: folder.name });
    });
    const current = folders.find((f) => f.id === activeFolderId);
    if (current) items.push({ id: current.id, folderId: current.id, label: current.name });
    folders.filter((f) => normalizeParentId(f.parentId) === activeFolderId).forEach((f) => {
      items.push({ id: f.id, folderId: f.id, label: f.name });
    });
    return items;
  }, [folders, activeFolderId, t]);

  const countLabel = useMemo(() => {
    if (!hasInitiallyLoaded) return '';
    return t('pages.bookmarks.savedCount').replace('%COUNT%', String(totalCount));
  }, [hasInitiallyLoaded, t, totalCount]);

  // Bookmarks are entirely account-bound — there's nothing public to show,
  // so logged-out visitors are bounced to the dashboard.
  if (!hasUser) {
    return <Redirect href="/app" />;
  }

  return (
    <View style={[styles.outerContainer, Platform.OS !== 'web' && { paddingBottom: insets.bottom }]}>
      <MetaHead title={t('pages.bookmarks.title')} description={t('pages.bookmarks.description')} noindex />

      <View style={styles.shell}>
        {!isXS && (
          <BookmarksRail
            allLabel={t('pages.bookmarks.allBookmarks')}
            allCount={!activeFolderId ? totalCount : undefined}
            foldersSectionLabel={t('pages.bookmarks.folders')}
            folders={folders}
            activeFolderId={activeFolderId}
            onSelectFolder={handleFolderPress}
            onCreateFolder={() => handleCreateFolder()}
            onRenameFolder={handleRenameFolder}
            onChangeIconFolder={handleChangeIcon}
            onDeleteFolder={handleDeleteFolder}
            onCreateSubfolder={handleCreateSubfolder}
            onMoveFolder={handleMoveFolder}
            t={t}
          />
        )}

        <View style={styles.body}>
          <View style={styles.contentWrap}>
            <View style={styles.chromeBar}>
              <View style={styles.pageHeader}>
                <Text tag="h1" style={styles.pageTitle}>
                  {activeFolder?.name || t('pages.bookmarks.title')}
                </Text>
                {!!countLabel && (
                  <Text size="s" color="secondary">
                    {countLabel}
                  </Text>
                )}
              </View>

              <View style={styles.topbarMainRow}>
                <View style={[styles.search, searchFocused && { borderColor: palette.activeBorder }]}>
                  <Icon name="search" size={14} color={colors.text_color_secondary} />
                  <TextInput
                    value={localSearch}
                    onChangeText={handleSearchChange}
                    placeholder={t('pages.bookmarks.searchPlaceholder')}
                    accessibilityLabel={t('pages.bookmarks.searchPlaceholder')}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    autoCorrect={false}
                    autoCapitalize="none"
                    placeholderTextColor={colors.text_color_tertiary}
                    style={styles.searchInput}
                  />
                  {localSearch.length > 0 ? (
                    <Pressable onPress={handleSearchClear} hitSlop={8} accessibilityRole="button" accessibilityLabel={t('common.clear')} style={{ width: 28, height: 28, alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="x" size={14} color={colors.text_color_tertiary} />
                    </Pressable>
                  ) : null}
                </View>
                <BookmarksSortDropdown value={sort} onChange={setSort} />
              </View>

              {isXS && (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.pillsRow}
                >
                  {mobilePillItems.map((pill) => {
                    const active = activeFolderId === pill.folderId;
                    return (
                      <Pressable
                        key={pill.id}
                        accessibilityRole="button"
                        accessibilityLabel={pill.label}
                        accessibilityState={{ selected: active }}
                        onPress={() => handleFolderPress(pill.folderId)}
                        style={[
                          styles.pill,
                          active && styles.pillActive,
                          Platform.OS === 'web' && { cursor: 'pointer' },
                        ]}
                      >
                        <Text
                          size="s"
                          weight={active ? 'semibold' : 'medium'}
                          numberOfLines={1}
                          color={active ? 'primary' : 'secondary'}
                        >
                          {pill.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('pages.bookmarks.newFolder')}
                    onPress={() => (activeFolder ? handleCreateSubfolder(activeFolder) : handleCreateFolder())}
                    style={[
                      styles.pill,
                      Platform.OS === 'web' && { cursor: 'pointer' },
                    ]}
                  >
                    <Icon name="plus" size={14} color={colors.text_color_secondary} />
                  </Pressable>
                </ScrollView>
              )}

              {/* When a folder is selected on mobile, show its edit/delete
                  actions inline beneath the pills (reliable on touch where
                  hover / long-press menus don't work). */}
              {isXS && activeFolder ? (
                <BookmarkFolderActions
                  folder={activeFolder}
                  onRename={handleRenameFolder}
                  onChangeIcon={handleChangeIcon}
                  onDelete={handleDeleteFolder}
                  onCreateSubfolder={handleCreateSubfolder}
                  onMove={handleMoveFolder}
                  t={t}
                  style={styles.mobileFolderActions}
                />
              ) : null}
            </View>

            {showEmpty ? (
              !searchQuery && !activeFolderId ? (
                <View style={{ flex: 1, minHeight: 0 }}>
                  <BookmarksEmptyMagazine onStartChat={handleEmptyMagazineCta} />
                </View>
              ) : (
                <View style={{ flex: 1 }}>
                  <EmptyState colors={colors} styles={styles} t={t} isFolder={!!activeFolderId} searchQuery={searchQuery} />
                </View>
              )
            ) : (
              <FlashList
                style={{ flex: 1 }}
                data={bookmarks}
                renderItem={renderItem}
                extraData={{ folders, activeFolderId, sort }}
                estimatedItemSize={168}
                keyExtractor={keyExtractor}
                showsVerticalScrollIndicator={false}
                ListFooterComponent={ListFooterComponent}
                contentContainerStyle={styles.listPad}
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
});

BookmarksPage.displayName = 'BookmarksPage';

export default function BookmarksRoute() {
  return <BookmarksPage />;
}
